import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MessageSquare } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';

const emptyForm = { client_name: '', content: '', rating: 5 };

const TestimonialsManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setMode('create');
    setForm(emptyForm);
    setCurrent(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setMode('edit');
    setCurrent(item);
    setForm({
      client_name: item.client_name || '',
      content: item.content || '',
      rating: item.rating || 5,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, getAuthHeader());
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        await axios.post(`${API}/testimonials`, form, getAuthHeader());
        toast.success('Testimonial added');
      } else {
        await axios.put(`${API}/testimonials/${current.id}`, form, getAuthHeader());
        toast.success('Testimonial updated');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div data-testid="testimonials-content">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <MessageSquare size={28} /> Testimonials
        </h2>
        <button
          onClick={openCreate}
          data-testid="add-testimonial-btn"
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gray-800"
        >
          <Plus size={16} /> Add Testimonial
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {items.map((item) => (
          <div key={item.id} className="bg-white border border-gray-200 p-6" data-testid={`testimonial-item-${item.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-heading font-bold">{item.client_name}</h3>
                <div className="flex gap-1 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className={i < item.rating ? 'text-yellow-400' : 'text-gray-300'}>★</span>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(item)} className="p-2 bg-gray-100 rounded hover:bg-gray-200" data-testid={`edit-testimonial-${item.id}`}>
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-100 text-red-600 rounded hover:bg-red-200" data-testid={`delete-testimonial-${item.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <p className="font-body text-gray-600 italic">"{item.content}"</p>
          </div>
        ))}
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={`${mode === 'create' ? 'Add' : 'Edit'} Testimonial`}
        footer={
          <button
            onClick={handleSubmit}
            disabled={!form.client_name || !form.content}
            data-testid="testimonial-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:opacity-50"
          >
            {mode === 'create' ? 'Add Testimonial' : 'Save Changes'}
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Client Name *</label>
            <input
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              data-testid="testimonial-form-name"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Rating *</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setForm({ ...form, rating: num })}
                  data-testid={`testimonial-form-rating-${num}`}
                  className={`text-2xl ${num <= form.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Review *</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={4}
              data-testid="testimonial-form-content"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TestimonialsManager;
